import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Spacer from "../../Spacer";
import Pulmonology from "../../icons/Pulmonology";

import { unpackSmoothSignal } from "../../../utils/rrLive/rgbSignalUnpack";

import {
  pickHrBpm,
  pickHrSignalQuality,
  pickRr,
  pickRrSignalQuality,
} from "./quality";
import { pickHeartDxHrBpm } from "./pickHrBpm";
import { useRrDetailsFromSmoothSignal } from "../../../hooks/results/useRrDetailsFromSmoothSignal";
import { formatValidatedMeasurement } from "../../../utils/results/validatedRanges";

function pickPackedSmooth(docData) {
  const r = docData?.results || docData?.resp?.results || null;
  return r?.rgb_smooth_signal ?? docData?.rgb_smooth_signal ?? null;
}

function getRrQualityMessage(rrSignalQuality, t) {
  if (rrSignalQuality !== "fail") return null;
  return {
    title: t("result.resp.qualityFailTitle", "Low respiratory signal quality."),
    subtitle: t("result.resp.qualityFailSubtitle", "Please try again."),
  };
}

function getHrQualityMessage(hrQuality, t) {
  if (hrQuality !== "bad") return null;
  return {
    title: t("result.pulse.qualityFailTitle", "Low heart-rate signal quality."),
    subtitle: t("result.pulse.qualityFailSubtitle", "Please try again."),
  };
}

function TidalBreathingResultViewImpl({ data, onFinish }) {
  const { t } = useTranslation();

  // RR
  const rr = useMemo(() => pickRr(data), [data]);
  const rrSignalQuality = useMemo(() => pickRrSignalQuality(data), [data]);

  // HR (from heartdx new location)
  const hr = useMemo(() => pickHeartDxHrBpm(data), [data]);
  const hrQuality = useMemo(() => pickHrSignalQuality(data), [data]);

  // Separate messages
  const rrMsg = useMemo(
    () => getRrQualityMessage(rrSignalQuality, t),
    [rrSignalQuality, t],
  );
  const hrMsg = useMemo(
    () => getHrQualityMessage(hrQuality, t),
    [hrQuality, t],
  );
  const rrDisplay = useMemo(() => formatValidatedMeasurement("rr", rr), [rr]);

  const rrIsBad = !!rrMsg;
  const hrIsBad = !!hrMsg;

  // Smooth signal (chart)
  const packedSmooth = useMemo(() => pickPackedSmooth(data), [data]);

  const smoothSignal = useMemo(
    () => unpackSmoothSignal(packedSmooth),
    [packedSmooth],
  );

  const maxSecs = useMemo(() => {
    if (!smoothSignal.length) return 60;
    return Math.max(6, Math.ceil(smoothSignal[smoothSignal.length - 1].t));
  }, [smoothSignal]);

  // Matrix/table derived from smooth signal (display-only)
  const rrDetails = useRrDetailsFromSmoothSignal({
    smoothSignal,
    overallRR: typeof rr === "number" && Number.isFinite(rr) ? rr : 0,
    perBreathMaxSec: 10,
    rangeMaxSec: 60,
  });

  return (
    <div className="w-full max-w-lg h-full min-h-0 overflow-y-auto">
      <section className="min-h-full p-4 flex flex-col items-center text-center gap-4">
        <h1 className="subtitle-text">
          {t("result.resp.completeTitle", "Respiratory scan complete")}
        </h1>

        {/* RR block */}
        <div className="flex flex-row items-center gap-6">
          <div
            className={[
              "w-14 h-14 flex items-center justify-center",
              rrIsBad ? "fill-greyscale-3" : "text-primary fill-primary",
            ].join(" ")}
          >
            <Pulmonology className="w-full h-full" />
          </div>

          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center justify-center">
              <div>{t("result.resp.rrLabel", "Respiratory Rate")}</div>
              <div
                className={[
                  "supertitle-text",
                  rrIsBad ? "!text-greyscale-3" : "text-primary",
                ].join(" ")}
              >
                {rrDisplay}
                <span className="muted-text">
                  {" "}
                  {t("result.resp.rrUnit", "br/min")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {rrMsg ? (
          <div className="flex flex-col items-center gap-1">
            <div className="muted-text">{rrMsg.title}</div>
            <div className="muted-text">{rrMsg.subtitle}</div>
          </div>
        ) : null}

        <Spacer />

        {/* HR block */}
        {/* <div className="flex flex-row items-center gap-6">
          <div
            className={[
              "w-14 h-14 flex items-center justify-center",
              hrIsBad ? "fill-greyscale-3" : "text-primary fill-primary",
            ].join(" ")}
          >
            <ECGHeart className="w-full h-full" />
          </div>

          <div className="flex flex-col items-center">
            <div className="flex flex-col items-center justify-center">
              <div>{t("result.pulse.hrLabel", "Heart Rate")}</div>
              <div
                className={[
                  "supertitle-text",
                  hrIsBad ? "!text-greyscale-3" : "text-primary",
                ].join(" ")}
              >
                {hr ?? "--"}
                <span className="muted-text">
                  {" "}
                  {t("result.pulse.unit", "bpm")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {hrMsg ? (
          <div className="flex flex-col items-center gap-1">
            <div className="muted-text">{hrMsg.title}</div>
            <div className="muted-text">{hrMsg.subtitle}</div>
          </div>
        ) : null}

        <Spacer /> */}

        <div className="mt-auto w-full pb-2">
          <button
            type="button"
            className="primary-button w-full"
            onClick={onFinish}
          >
            {t("result.finish", "Finish")}
          </button>
        </div>
      </section>

      {/* Chart */}
      {/* <section className="p-4 pt-2">
        <h2 className="subtitle-text">
          {t("result.resp.signalChartTitle", "Respiratory Signal")}
        </h2>

        {smoothSignal.length ? (
          <SignalChart
            smoothSignal={smoothSignal}
            peaks={[]}
            troughs={[]}
            height={196}
            maxSecs={maxSecs}
          />
        ) : (
          <div className="muted-text">
            {t("result.resp.noSignal", "No signal trace available.")}
          </div>
        )} */}

      {/* RR per-breath table (below matrix) */}
      {/* <RrPerBreathTable
          t={t}
          rows={rrDetails.rrPerBreath}
          unit={t("result.resp.rrUnit", "br/min")}
        />

        <RrRangeMatrix
          t={t}
          rrMatrix={rrDetails.rrMatrix}
          overallRR={typeof rr === "number" && Number.isFinite(rr) ? rr : 0}
          unit={t("result.resp.rrUnit", "br/min")}
          getHeatStyle={rrDetails.getHeatStyle}
          heatScaleMaxDelta={rrDetails.heatScale?.maxDelta}
        />
      </section> */}
    </div>
  );
}

const TidalBreathingResultView = memo(TidalBreathingResultViewImpl);

// Ready when we have RR (keep this behaviour unless you want to wait for HR too)
TidalBreathingResultView.isReady = (data) => pickRr(data) != null;

export default TidalBreathingResultView;
